import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useActivityFrequencyData } from "./hooks/useActivityFrequencyData";
import { useAutoLoad } from '../../../hooks/useAutoLoad';
import BaseTile from '../../../components/Tile/BaseTile';
import TileHeader, { TileHeaderButton } from '../../../components/Tile/TileHeader';
import TileContent, { TileChartContent, TileStatusContent } from '../../../components/Tile/TileContent';

// Custom Tooltip Component (count only on hover)
const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;

  const first = payload[0] || {};
  const value = first.value ?? first.payload?.count ?? 0;

  const numberFmt = (n) => {
    try { return new Intl.NumberFormat().format(Number(n) || 0); } catch { return String(n); }
  };

  return (
    <div className="bg-white border border-gray-300 px-2 py-1.5 rounded shadow-sm text-sm text-gray-800">
      {numberFmt(value)}
    </div>
  );
};

// Configuration Panel Component
const ConfigurationPanel = ({
  objectTypes,
  objectTypeActivities,
  selectedObjectType,
  selectedActivity,
  selectedTimeGranularity,
  selectedScope,
  setSelectedObjectType,
  setSelectedActivity,
  setSelectedTimeGranularity,
  setSelectedScope,
  onLoadChart,
  loading,
  hasFetched
}) => (
  <div className="bg-gray-50 border border-gray-200 rounded-md p-4 space-y-4">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Object Type Dropdown */}
      <div className="flex flex-col min-w-[200px]">
        <label className="text-sm font-medium text-gray-700 mb-1">
          Object Type
        </label>
        <select
          value={selectedObjectType}
          onChange={(e) => {
            setSelectedObjectType(e.target.value);
            setSelectedActivity("");
          }}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="">-- Select Object Type --</option>
          {objectTypes.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
      </div>

      {/* Activity Dropdown */}
      <div className="flex flex-col min-w-[200px]">
        <label className="text-sm font-medium text-gray-700 mb-1">
          Activity
        </label>
        <select
          value={selectedActivity}
          onChange={(e) => setSelectedActivity(e.target.value)}
          disabled={!selectedObjectType}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
        >
          <option value="">-- Select Activity --</option>
          {(objectTypeActivities[selectedObjectType] || []).map((activity) => (
            <option key={activity} value={activity}>
              {activity}
            </option>
          ))}
        </select>
      </div>

      {/* Time Granularity Dropdown */}
      <div className="flex flex-col min-w-[200px]">
        <label className="text-sm font-medium text-gray-700 mb-1">
          Time Granularity
        </label>
        <select
          value={selectedTimeGranularity}
          onChange={(e) => setSelectedTimeGranularity(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="Day">Day</option>
          <option value="Week">Week</option>
          <option value="Month">Month</option>
          <option value="Year">Year</option>
        </select>
      </div>

      {/* Scope Dropdown */}
      <div className="flex flex-col min-w-[200px]">
        <label className="text-sm font-medium text-gray-700 mb-1">
          Historical Intervals (3-24)
        </label>
        <select
          value={selectedScope}
          onChange={(e) => setSelectedScope(parseInt(e.target.value))}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          {Array.from({ length: 22 }, (_, i) => i + 3).map((num) => (
            <option key={num} value={num}>
              {num}{num === 10 && " (Default)"}
            </option>
          ))}
        </select>
      </div>
    </div>

    {/* Start/Reload Button */}
    <div className="flex justify-center pt-2">
      <button
        onClick={onLoadChart}
        disabled={!selectedObjectType || !selectedActivity || loading}
        className="px-6 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
      >
        {loading ? "Loading..." : hasFetched ? "Update Chart" : "Load Chart"}
      </button>
    </div>
  </div>
);

function ActivityFrequencyChart({ activeFile, onRefresh }) {
  const {
    // Data and fetch function
    chartData,
    loading,
    error,

    // Metadata
    objectTypes,
    objectTypeActivities,
    isMetadataLoaded,

    // Configuration state
    selectedObjectType,
    selectedActivity,
    selectedTimeGranularity,
    selectedScope,
    configurationVisible,

    // Setters
    setSelectedObjectType,
    setSelectedActivity,
    setSelectedTimeGranularity,
    setSelectedScope,
    setConfigurationVisible,

    // Actions
    fetchMetadata,
    fetchActivityFrequency,
  } = useActivityFrequencyData(activeFile);

  // Auto-load metadata when component mounts
  useAutoLoad(fetchMetadata, [activeFile], isMetadataLoaded);

  // Provide refresh function to parent (GridComponent)
  React.useEffect(() => {
    if (onRefresh) {
      onRefresh(() => {
        if (isMetadataLoaded && selectedActivity && selectedObjectType) {
          fetchActivityFrequency();
        }
      });
    }
  }, [onRefresh, isMetadataLoaded, selectedActivity, selectedObjectType, fetchActivityFrequency]);

  // Note: No auto-update on selection changes. The chart updates only when the user clicks the Load/Update button.

  // X-axis tick formatter based on selected time granularity
  const formatXAxisTick = React.useCallback((label) => {
    try {
      const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

      if (selectedTimeGranularity === 'Month') {
        // Try common formats like YYYY-MM
        const m = /^\s*(\d{4})-(\d{2})\s*$/.exec(label);
        if (m) {
          const mm = Math.max(1, Math.min(12, parseInt(m[2], 10)));
          return MONTH_NAMES[mm - 1]; // Jan, Feb, ...
        }
        // Fallback: try Date parsing
        const d = new Date(label);
        if (!Number.isNaN(d.getTime())) {
          return d.toLocaleString(undefined, { month: 'short' });
        }
        return label;
      }

      if (selectedTimeGranularity === 'Week') {
        // Expect labels like YYYY-W14 or include a week number somewhere
        const w = /W(\d{1,2})/.exec(label) || /-(\d{1,2})$/.exec(label);
        if (w) {
          const wk = parseInt(w[1], 10);
          return `week-${wk}`;
        }
        // Fallback: attempt ISO week from a parsable date
        const d = new Date(label);
        if (!Number.isNaN(d.getTime())) {
          const tmp = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
          const day = tmp.getUTCDay() || 7;
          tmp.setUTCDate(tmp.getUTCDate() + 4 - day);
          const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
          const wk = Math.ceil((((tmp - yearStart) / 86400000) + 1) / 7);
          return `week-${wk}`;
        }
        return label;
      }

      if (selectedTimeGranularity === 'Year') {
        const d = new Date(label);
        if (!Number.isNaN(d.getTime())) return String(d.getFullYear());
        const y = /^(\d{4})/.exec(label);
        return y ? y[1] : label;
      }

      // Day or default: return as-is (typically YYYY-MM-DD)
      return label;
    } catch {
      return label;
    }
  }, [selectedTimeGranularity]);

  const chartTitle = selectedActivity && selectedObjectType
    ? `Activity Frequency: '${selectedActivity}' on '${selectedObjectType}' (${selectedTimeGranularity}s, ${selectedScope} intervals)`
    : 'Activity Frequency Chart';

  const showLoadDataPrompt = !isMetadataLoaded && !loading && !error;
  const showChart = isMetadataLoaded && !loading && !error && selectedActivity && selectedObjectType && chartData.length > 0;
  const showNoData = isMetadataLoaded && !loading && !error && selectedActivity && selectedObjectType && chartData.length === 0;
  const showSelectionPrompt = isMetadataLoaded && !loading && !error && (!selectedActivity || !selectedObjectType);

  return (
    <BaseTile
      loading={loading}
      minHeight="lg"
      className="h-full"
    >
      <TileHeader
        layout="between"
        size="default"
        title={chartTitle}
        actions={
          <TileHeaderButton
            variant="secondary"
            size="sm"
            onClick={() => setConfigurationVisible(!configurationVisible)}
            aria-expanded={configurationVisible}
            aria-label={configurationVisible ? "Hide configuration" : "Show configuration"}
          >
            <svg
              className={`w-4 h-4 transition-transform duration-150 ${configurationVisible ? "rotate-180" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            <span className="ml-1">Configure</span>
          </TileHeaderButton>
        }
      />

      <TileContent layout="flex" direction="column" gap="lg" grow>
        {showLoadDataPrompt && (
          <TileStatusContent
            status="info"
            title="Load Object Types"
            message="Click the button to load available object types and activities to configure the chart."
            action={
              <button
                onClick={fetchMetadata}
                className="px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              >
                Load Data
              </button>
            }
            icon={
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 7v10m0-10l8 6m8-6v10m0-10l-8 6m-4 4h16" />
              </svg>
            }
          />
        )}

        {isMetadataLoaded && configurationVisible && (
          <ConfigurationPanel
            objectTypes={objectTypes}
            objectTypeActivities={objectTypeActivities}
            selectedObjectType={selectedObjectType}
            selectedActivity={selectedActivity}
            selectedTimeGranularity={selectedTimeGranularity}
            selectedScope={selectedScope}
            setSelectedObjectType={setSelectedObjectType}
            setSelectedActivity={setSelectedActivity}
            setSelectedTimeGranularity={setSelectedTimeGranularity}
            setSelectedScope={setSelectedScope}
            onLoadChart={async () => {
              const ok = await fetchActivityFrequency();
              if (ok) {
                // AC1: auto-collapse configuration panel after successful Start/Reload
                setConfigurationVisible(false);
              }
            }}
            loading={loading}
            hasFetched={chartData.length > 0}
          />
        )}

        {showChart && (
          <TileChartContent height={440} className="flex-grow">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                margin={{ top: 20, right: 24, left: 16, bottom: 56 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="label"
                  type="category"
                  tickFormatter={formatXAxisTick}
                  interval={0}
                  allowDuplicatedCategory={false}
                  tickMargin={12}
                  minTickGap={8}
                  height={70}
                  tick={{ angle: -45, textAnchor: 'end' }}
                />
                <YAxis
                  label={{
                    value: "Event Count",
                    angle: -90,
                    position: "insideLeft",
                  }}
                  allowDecimals={false}
                />
                {/**
                 * Use component reference instead of instantiated element so Recharts
                 * reliably injects (active, payload, label) props in all versions.
                 */}
                <Tooltip
                  content={CustomTooltip}
                  wrapperStyle={{ zIndex: 9999 }}
                  cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                />
                <Bar dataKey="count" fill="#3b82f6" name="Activity Frequency" isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          </TileChartContent>
        )}

        {error && (
          <TileStatusContent
            status="error"
            title="Failed to load chart"
            message={typeof error === 'string' ? error : 'An unexpected error occurred while loading data.'}
            action={
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    if (isMetadataLoaded && selectedActivity && selectedObjectType) {
                      fetchActivityFrequency();
                    } else {
                      fetchMetadata();
                    }
                  }}
                  className="px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                >
                  Retry
                </button>
                {!configurationVisible && (
                  <button
                    onClick={() => setConfigurationVisible(true)}
                    className="px-4 py-2 bg-gray-200 text-gray-800 text-sm font-medium rounded-md hover:bg-gray-300 transition-colors duration-200"
                  >
                    Open Configuration
                  </button>
                )}
              </div>
            }
          />
        )}

        {showNoData && (
          <TileStatusContent
            status="empty"
            title="No Data Available"
            message="No events found for the selected activity, object type, and time configuration."
            icon={
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            }
          />
        )}

        {showSelectionPrompt && (
          <TileStatusContent
            status="empty"
            title="Configure Chart Settings"
            message="Please select both an object type and activity from the configuration panel above to view the frequency chart."
            action={
              !configurationVisible && (
                <button
                  onClick={() => setConfigurationVisible(true)}
                  className="px-4 py-2 bg-success-500 text-white text-sm font-medium rounded-md hover:bg-success-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-success-500 focus:ring-offset-2"
                >
                  Show Configuration
                </button>
              )
            }
            icon={
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                      d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
              </svg>
            }
          />
        )}
      </TileContent>
    </BaseTile>
  );
}

export default ActivityFrequencyChart;
